/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { Button } from '@mui/material'
import { blockUserByAdmin, blockUserByAdminVariables } from '__generatedTypes__/blockUserByAdmin'
import { BLOCK_USER_BY_ADMIN, GET_USERS_COUNT_BY_ROLES, UNBLOCK_USER_BY_ADMIN } from 'queries/users'
import { unblockUserByAdmin, unblockUserByAdminVariables } from '__generatedTypes__/unblockUserByAdmin'
import { findAllUsersAccountSharing_findAllUsersAccountSharing_items } from '__generatedTypes__/findAllUsersAccountSharing'
import { INITIAL_REASON } from 'pagesNew/Users/Edit/components/UserManagement'
import { toast } from 'utils/toast'
import * as classes from '../styles'

type Props = {
  accountSharingItem: findAllUsersAccountSharing_findAllUsersAccountSharing_items
  setError: (error: string) => void
  refetch: () => void
}

export const BlockButton: FC<Props> = ({ accountSharingItem, setError, refetch }) => {
  const apolloClient = useApolloClient()
  const [blockUser, { loading: isBlockingRequestInFlight }] = useMutation<blockUserByAdmin, blockUserByAdminVariables>(
    BLOCK_USER_BY_ADMIN
  )
  const [unBlockUser, { loading: isUnBlockingRequestInFlight }] = useMutation<
    unblockUserByAdmin,
    unblockUserByAdminVariables
  >(UNBLOCK_USER_BY_ADMIN)

  const handleBlockUserClick = (userId: string, isBlocked: boolean) => async () => {
    const options = {
      variables: {
        userId,
        reason: INITIAL_REASON,
      },
    }
    const action = isBlocked ? unBlockUser : blockUser

    try {
      await action(options)

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'UsersAccountSharingByAdmin',
          id: userId,
        }),
        fields: {
          isBlocked: () => !isBlocked,
        },
      })

      apolloClient.cache.updateQuery(
        {
          query: GET_USERS_COUNT_BY_ROLES,
        },
        (previousData) => ({
          ...previousData,
          findUsersCountByRoles: {
            ...previousData.findUsersCountByRoles,
            blocked: isBlocked
              ? previousData.findUsersCountByRoles.blocked - 1
              : previousData.findUsersCountByRoles.blocked + 1,
          },
        })
      )

      toast.success(`User with email: ${accountSharingItem.email} has been ${isBlocked ? 'unblocked' : 'blocked'}`)

      refetch()
    } catch (error) {
      const errorMessage = `An error occurred while trying to ${isBlocked ? 'unblock' : 'block'} user with email: ${
        accountSharingItem.email
      }`
      setError(errorMessage)
      toast.error(errorMessage)
    }
  }

  return (
    <Button
      size="small"
      variant="outlined"
      color={accountSharingItem.isBlocked ? 'blue' : 'red'}
      css={classes.bockButton}
      disabled={isBlockingRequestInFlight || isUnBlockingRequestInFlight}
      onClick={handleBlockUserClick(accountSharingItem.id, accountSharingItem.isBlocked)}
    >
      {accountSharingItem.isBlocked ? 'Unblock' : 'Block'}
    </Button>
  )
}
